import { useCallback } from "react"
import { useMutation } from "urql"

import type { UpdateVendorPayableFieldsInput } from "../utils/vendorPayableFields"

import { UpdateVendorPayableFieldsDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UpdateVendorPayableFieldsOptions {
  silent?: boolean
}

export interface UseUpdateVendorPayableFieldsReturn {
  updateVendorPayableFields: (
    input: UpdateVendorPayableFieldsInput,
    options?: UpdateVendorPayableFieldsOptions
  ) => Promise<boolean>
  isUpdating: boolean
}

export const useUpdateVendorPayableFields = (): UseUpdateVendorPayableFieldsReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation(UpdateVendorPayableFieldsDocument)

  const updateVendorPayableFields = useCallback(
    async (input: UpdateVendorPayableFieldsInput, options: UpdateVendorPayableFieldsOptions = {}): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.company.update
      const hasError = error || output?.__typename === "CompanyUpdateFailure"

      if (hasError) {
        const message = output?.__typename === "CompanyUpdateFailure" ? output.error.message : error?.message
        notify({
          type: NotificationType.Error,
          message: message ?? "Failed to save vendor payable info. Please try again.",
        })
        return false
      }

      if (!options.silent) {
        notify({ type: NotificationType.Success, message: "Vendor payable info saved" })
      }
      return true
    },
    [executeMutation, notify]
  )

  return { updateVendorPayableFields, isUpdating: fetching }
}
